import { api } from '../api';
import type { EstadoPersona } from '../estado-persona';
import type { BinarySyncLegajo } from './sync-binary.service';

export type BusquedaMatchTipo = 'titular' | 'familiar';

/** Estado real del afiliado en Binary/PMED, denormalizado en el legajo. */
export interface Ms2AltaResumen {
  afiliadoActivo: boolean;
  plan: 'MS1' | 'MS2' | 'otro' | null;
  planCodigo?: string | null;
  fechaAltaBinary?: string | null;
  estadoAfi?: string | null;
  syncedAt?: string;
}

/** Resumen del pago MS2 del legajo. */
export interface PagoResumen {
  estado: string;
  monto?: number;
  actualizadoAt?: string;
  conciliadoBinary?: boolean;
}

export interface BusquedaMatchItem {
  asociadoId: string;
  documento: string;
  cuil?: string;
  apellSolt: string;
  nombres: string;
  parentesco: string;
  fechaNacimiento?: string;
  legajoId: string;
  legajoNumero: string;
  legajoEstado: string;
  matchTipo: BusquedaMatchTipo;
  titularNombre: string;
  padronId: string;
  padronNombre: string;
  padronEstado: string;
  empresaId: string;
  empresaNombre?: string;
  ms2Alta?: Ms2AltaResumen | null;
  pagoEstado?: string | null;
  /** True si la persona está bloqueada a nivel individuo. */
  bloqueado?: boolean;
  /** True si la persona está de baja en Binary (ESTADO=B), a nivel individuo. */
  bajaBinary?: boolean;
  /** Motivo de la baja (micrositio, o el de Binary como fallback). */
  bajaMotivo?: string;
  /** Fecha desde la que rige la baja (`bajaBinary.fechaVigencia`). La baja se
   * carga en Binary con vigencia futura: hasta esa fecha la persona conserva
   * la cobertura, asi que sin este dato el estado "De baja" es ambiguo. */
  bajaVigencia?: string;
  /** Estado consolidado de la persona (independiente del estado del legajo). */
  estadoPersona?: EstadoPersona;
}

export interface BusquedaResultado {
  total: number;
  truncado: boolean;
  matches: BusquedaMatchItem[];
}

export async function buscarPersonas(q: string): Promise<BusquedaResultado> {
  const { data } = await api.get<BusquedaResultado>('/busqueda', {
    params: { q },
  });
  return data;
}

export interface LegajoDetalleAsociado {
  id: string;
  parentesco: string;
  apellSolt: string;
  nombres: string;
  documento: string;
  cuil?: string;
  fechaNacimiento?: string;
  sexo?: string;
  estadoCivil?: string;
  nacionalidad?: string;
  tipoDoc?: string;
  domicilio?: string;
  numero?: string;
  piso?: string;
  depto?: string;
  cp?: string;
  email?: string;
  areaCelular?: string;
  numeroCelular?: string;
  renaperEstado?: string;
  /** Bloqueo a nivel persona (frenada individualmente). */
  bloqueado?: boolean;
  bloqueadoMotivo?: string;
  /** La persona no existe en Binary de ninguna forma (`asociado.noEnBinary`). */
  noEnBinary?: boolean;
  noEnBinaryMotivo?: string;
  /** Última vez que la sincronización con Binary escribió en la persona (ISO). */
  sincronizadoBinaryAt?: string;
  /** Baja en Binary (ESTADO=B) a nivel persona (p. ej. fallecimiento). */
  bajaBinary?: boolean;
  bajaBinaryMotivo?: string;
  /** Motivo de la baja en el micrositio (ej. fallecimiento). */
  bajaMotivo?: string;
  /** Fecha desde la que rige la baja (`bajaBinary.fechaVigencia`). La baja se
   * carga en Binary con vigencia futura: hasta esa fecha la persona conserva
   * la cobertura, asi que sin este dato el estado "De baja" es ambiguo. */
  bajaVigencia?: string;
  /** Estado consolidado de la persona (independiente del estado del legajo). */
  estadoPersona?: EstadoPersona;
}

export interface LegajoDetalleObservacion {
  campo: string;
  asociadoId: string;
  valorPropuesto?: string;
  mensaje?: string;
  reportadoAt: string;
  resuelta?: boolean;
  resueltaAt?: string;
}

export interface LegajoDetalle {
  legajo: {
    id: string;
    numero: string;
    estado: string;
    activacionExpiraAt?: string;
    activacionCompletadaAt?: string;
    planSuperadorElegido?: boolean | null;
    interesContactoMs2?: boolean | null;
    altaBatchId?: string;
    binaryAfiId?: string;
    binaryMedicard?: string;
    binaryAltaAt?: string;
    createdAt?: string;
    updatedAt?: string;
    ms2Alta?: Ms2AltaResumen | null;
    pago?: PagoResumen | null;
    /** Última corrida de la sincronización Binary → portal (VEN-540). */
    binarySync?: BinarySyncLegajo | null;
  };
  padron: {
    id: string;
    nombreArchivo: string;
    estado: string;
    fechaSubida?: string;
  };
  empresa: {
    id: string;
    nombre?: string;
  };
  titular: LegajoDetalleAsociado;
  familiares: LegajoDetalleAsociado[];
  observaciones: LegajoDetalleObservacion[];
  infoComercial: {
    montoMs2: number;
    elegibleParaOferta: boolean;
    motivoNoElegible?: string;
    /** VEN-491: viene solo si el titular estuvo en MS2 en los últimos 12 meses. */
    ms2Reciente?: string;
    ofertaTmkVigente?: {
      generadoPor: { nombre?: string; email?: string };
      generadoAt: string;
      expiraAt: string;
      yaAceptada: boolean;
    };
  };
  /** Otros legajos con el mismo número (bis/real conviviendo). */
  legajosHermanos?: Array<{
    id: string;
    numero: string;
    estado: string;
    titularNombre: string;
    titularDocumento: string;
  }>;
}

export async function getLegajoDetalle(legajoId: string): Promise<LegajoDetalle> {
  const { data } = await api.get<LegajoDetalle>(`/busqueda/legajo/${legajoId}`);
  return data;
}

/**
 * Detalle de un grupo en un padrón BORRADOR (todavía sin legajo). Se arma desde
 * los asociados del padrón (titular + familiares por número de legajo). Lo usa
 * la búsqueda cuando el match no tiene `legajoId` (padrón sin cerrar).
 */
export async function getGrupoBorradorDetalle(
  padronId: string,
  numero: string,
): Promise<LegajoDetalle> {
  const { data } = await api.get<LegajoDetalle>('/busqueda/grupo', {
    params: { padronId, numero },
  });
  return data;
}

/**
 * "Reportar como interesado": marca al titular como interesado en el cambio a
 * MS2 y lo mete en la rueda de SkyTel (el dialer lo llama). Devuelve si se
 * generó ahora o si ya estaba sincronizado.
 */
export async function reportarInteresadoSkytel(
  legajoId: string,
): Promise<{ resultado: 'generado' | 'ya_estaba'; legajoNumero: string }> {
  const { data } = await api.post<{ resultado: 'generado' | 'ya_estaba'; legajoNumero: string }>(
    `/admin/skytel-sync/legajo/${legajoId}/reportar-interesado`,
    {},
  );
  return data;
}
